/**
 * Adoption Statistics Page
 * User adoption metrics and interaction analytics for the platform
 */
import { useState, useMemo } from 'react'
import { PageShell } from '@/components/common/PageShell'
import { StatCard } from '@/components/common/StatCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { 
  Users, 
  RefreshCw,
  TrendingUp,
  Activity,
  BarChart3,
  AlertCircle,
} from 'lucide-react'
import { PERIOD_OPTIONS, getDateRange, type PeriodValue } from '@/lib/constants'
import {
  useAdoptionSummary,
  useUsersOverTime,
  useSessionsByMode,
  useSessionOutcomes,
  useTriggerSources,
  useTopUsers,
} from '@/api/adoption'
import {
  ChartCard,
  UsersOverTimeChart,
  SessionsByModeChart,
  StatusDistributionChart,
  HorizontalBarChart,
} from '@/components/charts'
import { statusColors, chartColors } from '@/components/charts/chartConfig'
import { TopUsersTable } from '@/components/data-display'

/**
 * Map session status to a display color
 */
function getStatusColor(status: string): string {
  const normalizedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  return statusColors[normalizedStatus] || chartColors.muted
}

/**
 * Adoption Statistics Page Component
 */
export function AdoptionStatsPage() {
  const [period, setPeriod] = useState<PeriodValue>('7d')
  
  const dateRange = useMemo(() => getDateRange(period), [period])

  // API hooks
  const summaryQuery = useAdoptionSummary({ from: dateRange.from, to: dateRange.to })
  const usersOverTimeQuery = useUsersOverTime({ from: dateRange.from, to: dateRange.to })
  const sessionsByModeQuery = useSessionsByMode({ from: dateRange.from, to: dateRange.to })
  const sessionOutcomesQuery = useSessionOutcomes({ from: dateRange.from, to: dateRange.to })
  const triggerSourcesQuery = useTriggerSources({ from: dateRange.from, to: dateRange.to })
  const topUsersQuery = useTopUsers({ from: dateRange.from, to: dateRange.to })

  const handleRefresh = () => {
    summaryQuery.refetch()
    usersOverTimeQuery.refetch()
    sessionsByModeQuery.refetch()
    sessionOutcomesQuery.refetch()
    triggerSourcesQuery.refetch()
    topUsersQuery.refetch()
  }

  // Transform session outcomes to StatusDistributionChart format
  const outcomeChartData = useMemo(() => {
    if (!sessionOutcomesQuery.data?.data) return []
    return sessionOutcomesQuery.data.data.map(item => ({
      status: item.status,
      count: item.count,
      color: getStatusColor(item.status),
    }))
  }, [sessionOutcomesQuery.data])

  // Transform trigger sources to HorizontalBarChart format
  const triggerChartData = useMemo(() => {
    if (!triggerSourcesQuery.data?.data) return []
    return triggerSourcesQuery.data.data.map(item => ({
      name: item.source,
      value: item.count,
    }))
  }, [triggerSourcesQuery.data])

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6" />
              Adoption Analytics
            </h1>
            <p className="text-muted-foreground text-sm">
              Track user adoption and platform engagement metrics
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Period selector */}
            <Select value={period} onValueChange={(v) => setPeriod(v as PeriodValue)}>
              <SelectTrigger className="w-[160px]" data-testid="period-selector">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Refresh button */}
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Total Users"
            value={summaryQuery.data?.totalUsers ?? 0}
            variant="default"
            isLoading={summaryQuery.isLoading}
          />
          <StatCard
            icon={TrendingUp}
            label="Active Users"
            value={summaryQuery.data?.activeUsers ?? 0}
            variant="success"
            isLoading={summaryQuery.isLoading}
          />
          <StatCard
            icon={Activity}
            label="Total Sessions"
            value={summaryQuery.data?.totalSessions ?? 0}
            variant="default"
            isLoading={summaryQuery.isLoading}
          />
          <StatCard
            icon={BarChart3}
            label="Avg Sessions/User"
            value={(summaryQuery.data?.avgSessionsPerUser ?? 0).toFixed(1)}
            variant="default"
            isLoading={summaryQuery.isLoading}
          />
        </div>

        {/* Summary Error State */}
        {summaryQuery.error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load summary stats: {summaryQuery.error.message}
            </AlertDescription>
          </Alert>
        )}

        {/* User Activity Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard 
            title="Users Over Time" 
            description="Daily unique and new users"
            isLoading={usersOverTimeQuery.isLoading}
            error={usersOverTimeQuery.error?.message}
            isEmpty={!usersOverTimeQuery.data?.data?.length}
          >
            <UsersOverTimeChart 
              data={usersOverTimeQuery.data?.data ?? []} 
              showNewUsers 
            />
          </ChartCard>

          <ChartCard 
            title="Sessions by Mode" 
            description="Distribution of session types"
            isLoading={sessionsByModeQuery.isLoading}
            error={sessionsByModeQuery.error?.message}
            isEmpty={!sessionsByModeQuery.data?.data?.length}
          >
            <SessionsByModeChart 
              data={sessionsByModeQuery.data?.data ?? []} 
            />
          </ChartCard>
        </div>

        {/* Session Breakdown Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard 
            title="Session Outcomes" 
            description="Success vs failure distribution"
            isLoading={sessionOutcomesQuery.isLoading}
            error={sessionOutcomesQuery.error?.message}
            isEmpty={!sessionOutcomesQuery.data?.data?.length}
          >
            <StatusDistributionChart 
              data={outcomeChartData}
              showCenterLabel
              centerLabelText="Sessions"
            />
          </ChartCard>

          <ChartCard 
            title="Trigger Sources" 
            description="How sessions are initiated"
            isLoading={triggerSourcesQuery.isLoading}
            error={triggerSourcesQuery.error?.message}
            isEmpty={!triggerSourcesQuery.data?.data?.length}
          >
            <HorizontalBarChart 
              data={triggerChartData}
              ariaLabel="Sessions by trigger source"
            />
          </ChartCard>
        </div>

        {/* Top Users Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium">Top Users</CardTitle>
          </CardHeader>
          <CardContent>
            {topUsersQuery.error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Failed to load top users: {topUsersQuery.error.message}
                </AlertDescription>
              </Alert>
            ) : (
              <TopUsersTable 
                data={topUsersQuery.data?.data ?? []}
                isLoading={topUsersQuery.isLoading}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </PageShell>
  )
}
